import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { initializeGlobalState, sendPackageOperationalEvent, sendTrackEvent, sendUIEvent } from '../../../common/utils';
import { BSCIntlProvider } from '../../../common/utils/locale';
import { Logger } from '../../../common/utils/logger';
import { ACCEPT_ALL_PREFERENCES, ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY, ONLY_NECESSARY_PREFERENCES } from '../../../constants';
import { deleteCookie, getCookie } from '../../../controllers/cookie-controls';
import { updatePreferences } from '../../../controllers/preferences-controls/update-preferences';
import { i18n } from '../messages';
import { PreferenceModal } from '../preference-modal';
import usePreferences from '../preference-modal/use-preferences';
import { PresentationalConsentBanner } from '../presentational-consent-banner';
import useBannerVisibility from '../use-banner-visibility';
import usePrevious from '../use-previous';
export var ConsentBannerImpl = function ConsentBannerImpl(_ref) {
  var onShow = _ref.onShow,
    onHide = _ref.onHide,
    initialPreferences = _ref.initialPreferences,
    _ref$minWidth = _ref.minWidth,
    minWidth = _ref$minWidth === void 0 ? 400 : _ref$minWidth,
    product = _ref.product,
    onConsentBannerBailOut = _ref.onConsentBannerBailOut,
    _ref$analyticsEnabled = _ref.analyticsEnabled,
    analyticsEnabled = _ref$analyticsEnabled === void 0 ? true : _ref$analyticsEnabled,
    _ref$localConsentMode = _ref.localConsentMode,
    localConsentMode = _ref$localConsentMode === void 0 ? false : _ref$localConsentMode,
    newMinHeight = _ref.newMinHeight;
  var firstRender = useRef(true);
  useEffect(function () {
    if (!firstRender.current) {
      return;
    }

    // Send an operational event if the banner is shown after previously having
    // been dismissed. This will help prevent issues with the banner
    // inappropriately reappearing.
    var lastBannerDismissal = getCookie(ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY);
    if (lastBannerDismissal) {
      var timeSinceDismissal = Date.now() - new Date(Number.parseInt(lastBannerDismissal)).getTime();
      var lessThanOneHour = timeSinceDismissal < 1000 * 60 * 60;
      var lessThanOneDay = timeSinceDismissal < 1000 * 60 * 60 * 24;
      var lessThanOneWeek = timeSinceDismissal < 1000 * 60 * 60 * 24 * 7;
      sendPackageOperationalEvent({
        action: 'bannerAppearanceAfterDismissal',
        attributes: {
          timeSinceDismissal: timeSinceDismissal,
          lessThanOneHour: lessThanOneHour,
          lessThanOneDay: lessThanOneDay,
          lessThanOneWeek: lessThanOneWeek
        }
      });
      deleteCookie(ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY);
    }
    firstRender.current = false;
  }, []);
  useEffect(function () {
    initializeGlobalState({
      analyticsEnabled: analyticsEnabled,
      product: product,
      localConsentMode: localConsentMode
    });
  }, [analyticsEnabled, product, localConsentMode]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isPreferencesModalOpen = _useState2[0],
    setIsPreferencesModalOpen = _useState2[1];
  var intl = useIntl();
  var _useFlags = useFlags(),
    showFlag = _useFlags.showFlag;
  var _usePreferences = usePreferences({
      initialPreferences: initialPreferences
    }),
    preferences = _usePreferences.preferences,
    isPrefetchedPreferencesState = _usePreferences.isPrefetchedPreferencesState,
    isInitialRender = _usePreferences.isInitialRender;
  var _useBannerVisibility = useBannerVisibility({
      preferences: preferences,
      isPrefetchedPreferencesState: isPrefetchedPreferencesState,
      initialPreferences: initialPreferences,
      isInitialRender: isInitialRender
    }),
    showBanner = _useBannerVisibility.showBanner,
    setIsDismissed = _useBannerVisibility.setIsDismissed;
  var closePreferenceModal = useCallback(function () {
    setIsPreferencesModalOpen(false);
  }, [setIsPreferencesModalOpen]);
  var onPreferenceModalSubmit = useCallback(function () {
    closePreferenceModal();
    setIsDismissed(true);
  }, [closePreferenceModal, setIsDismissed]);
  var onClickPreferences = useCallback(function () {
    setIsPreferencesModalOpen(true);
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'preferences',
      source: 'cookieConsentBanner'
    });
  }, []);
  var showSuccessFlag = useCallback(function () {
    if (localConsentMode) {
      showFlag(_objectSpread(_objectSpread({}, successToastProps()), {}, {
        id: 'cookieConsentBanner',
        description: intl.formatMessage(i18n.successFlagDescriptionLocalOnly),
        title: intl.formatMessage(i18n.successFlagTitle)
      }));
    } else {
      showFlag(_objectSpread(_objectSpread({}, successToastProps()), {}, {
        id: 'cookieConsentBanner',
        description: intl.formatMessage(i18n.successFlagDescription),
        title: intl.formatMessage(i18n.successFlagTitle)
      }));
    }
  }, [intl, localConsentMode, showFlag]);
  var showErrorFlag = useCallback(function () {
    showFlag(_objectSpread(_objectSpread({}, errorToastProps()), {}, {
      id: 'cookieConsentBanner',
      description: intl.formatMessage(i18n.errorFlagDescription),
      title: intl.formatMessage(i18n.errorFlagTitle)
    }));
  }, [intl, showFlag]);

  // Sets preferences to allow only Strictly Necessary cookies
  var onClickOnlyNecessary = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(displayedTextMap) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;
            sendUIEvent({
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'onlyNecessary',
              source: 'cookieConsentBanner'
            });
            _context.next = 4;
            return updatePreferences(ONLY_NECESSARY_PREFERENCES, displayedTextMap);
          case 4:
            sendTrackEvent({
              source: 'cookieConsentBanner',
              action: 'submitted',
              actionSubject: 'cookieConsentPreferences'
            });
            showSuccessFlag();
            _context.next = 12;
            break;
          case 8:
            _context.prev = 8;
            _context.t0 = _context["catch"](0);
            Logger.errorWithOperationalEvent({
              action: 'updatePreferencesError',
              attributes: {
                preferences: ONLY_NECESSARY_PREFERENCES,
                product: product
              },
              message: "Failed to update preferences from banner. ".concat(_context.t0.message || '')
            });
            showErrorFlag();
          case 12:
            _context.prev = 12;
            setIsDismissed(true);
            return _context.finish(12);
          case 15:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[0, 8, 12, 15]]);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [product, showErrorFlag, showSuccessFlag, setIsDismissed]);

  // Sets preferences to allow all categories of cookies
  var onClickAcceptAll = useCallback( /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(displayedTextMap) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            sendUIEvent({
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'acceptAll',
              source: 'cookieConsentBanner'
            });
            _context2.next = 4;
            return updatePreferences(ACCEPT_ALL_PREFERENCES, displayedTextMap);
          case 4:
            sendTrackEvent({
              source: 'cookieConsentBanner',
              action: 'submitted',
              actionSubject: 'cookieConsentPreferences'
            });
            showSuccessFlag();
            _context2.next = 12;
            break;
          case 8:
            _context2.prev = 8;
            _context2.t0 = _context2["catch"](0);
            Logger.errorWithOperationalEvent({
              action: 'updatePreferencesError',
              attributes: {
                preferences: ACCEPT_ALL_PREFERENCES,
                product: product
              },
              message: "Failed to update preferences from banner. ".concat(_context2.t0.message || '')
            });
            showErrorFlag();
          case 12:
            _context2.prev = 12;
            setIsDismissed(true);
            return _context2.finish(12);
          case 15:
          case "end":
            return _context2.stop();
        }
      }, _callee2, null, [[0, 8, 12, 15]]);
    }));
    return function (_x2) {
      return _ref3.apply(this, arguments);
    };
  }(), [product, showErrorFlag, showSuccessFlag, setIsDismissed]);
  var onClickTrackingNoticeLink = useCallback(function () {
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'cookiesTrackingNotice',
      source: 'cookieConsentBanner'
    });
  }, []);

  // Call onShow/onHide as a side effect of showBanner changing, rather than
  // possibly having to call them in multiple places.
  var prevShowBanner = usePrevious(showBanner);
  useEffect(function () {
    if (showBanner) {
      addUFOCustomData({
        isCookieConsentBannerDisplayed: true
      });
    }
    if (showBanner && (!prevShowBanner || fg('platform_fix_blank_cookie_banner_bug'))) {
      onShow === null || onShow === void 0 || onShow();
    } else if (!showBanner && (prevShowBanner || fg('platform_fix_blank_cookie_banner_bug'))) {
      onHide === null || onHide === void 0 || onHide();
    }
  }, [showBanner, prevShowBanner, onShow, onHide]);
  useEffect(function () {
    // when banner is not visible and isPrefetchedPreferencesState is in "resolved" state
    if (!showBanner && isPrefetchedPreferencesState !== undefined) {
      // report "banner decided not to show" to sync decision with other components
      onConsentBannerBailOut === null || onConsentBannerBailOut === void 0 || onConsentBannerBailOut();
    }
  }, [showBanner, isPrefetchedPreferencesState, onConsentBannerBailOut]);
  if (!showBanner) {
    return null;
  }
  return /*#__PURE__*/React.createElement(FlagsProvider, null, /*#__PURE__*/React.createElement(BSCIntlProvider, null, /*#__PURE__*/React.createElement(PresentationalConsentBanner, {
    onClickOnlyNecessary: onClickOnlyNecessary,
    onClickAcceptAll: onClickAcceptAll,
    onClickPreferences: onClickPreferences,
    onClickTrackingNoticeLink: onClickTrackingNoticeLink,
    minWidth: minWidth,
    newMinHeight: newMinHeight
  }), isPreferencesModalOpen && /*#__PURE__*/React.createElement(PreferenceModal, {
    onClose: closePreferenceModal,
    onSubmit: onPreferenceModalSubmit,
    showSuccessFlag: showSuccessFlag,
    showErrorFlag: showErrorFlag
  })));
};